import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { communicationsService, CommunicationChannel } from '@services/communicationsService';
import { formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

type CommunicationLog = {
  id: string;
  channel: CommunicationChannel | string;
  status: string;
  templateKey?: string;
  errorCode?: string;
  errorMessage?: string;
  retryCount?: number;
  nextRetryAt?: string;
  contentSnapshot?: string;
  createdAt: string;
  clientId?: string;
  appointmentId?: string;
};

const CommunicationManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logs, setLogs] = useState<CommunicationLog[]>([]);
  const [channelStatus, setChannelStatus] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [attemptsById, setAttemptsById] = useState<Record<string, any[]>>({});

  const [form, setForm] = useState({
    clientId: '',
    appointmentId: '',
    channel: 'in_app' as CommunicationChannel,
    templateKey: '',
    subject: '',
    content: '',
  });

  const availableChannels = useMemo(
    () => [
      { value: 'in_app' as const, label: 'In-app' },
      { value: 'email' as const, label: 'Email' },
      { value: 'sms' as const, label: 'SMS' },
      { value: 'push' as const, label: 'Push' },
    ],
    [],
  );

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const [data, channels, templateRows] = await Promise.all([
        communicationsService.list({ limit: 50 }),
        communicationsService.getChannelStatus(),
        communicationsService.listTemplates(),
      ]);
      setLogs(Array.isArray(data) ? data : []);
      setChannelStatus(channels);
      setTemplates(Array.isArray(templateRows) ? templateRows : []);
    } catch (err) {
      console.error('Eroare la incarcarea logurilor de comunicare:', err);
      setError('Nu am putut încărca logurile de comunicare.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const sendCommunication = async () => {
    setSending(true);
    setError('');
    setSuccess('');
    try {
      if (!form.clientId.trim()) {
        setError('clientId este obligatoriu.');
        return;
      }
      const response = await communicationsService.send({
        client_id: form.clientId.trim(),
        appointment_id: form.appointmentId.trim() || undefined,
        channel: form.channel,
        template_key: form.templateKey.trim() || undefined,
        subject: form.subject.trim() || undefined,
        content: form.content.trim() || undefined,
      });
      if (!response.success) {
        setError(response.data?.error_message || response.data?.error_code || 'Trimiterea a eșuat.');
      } else {
        setSuccess(`Mesaj trimis cu status: ${response.data.status}`);
      }
      await loadLogs();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.data?.error_message || err?.message || 'Eroare la trimitere.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const retryCommunication = async (id: string) => {
    setError('');
    setSuccess('');
    try {
      const response = await communicationsService.retry(id);
      if (!response.success) {
        setError(response.data?.error_message || 'Retry eșuat.');
      } else {
        setSuccess('Retry executat.');
      }
      await loadLogs();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Retry eșuat.');
    }
  };

  const loadAttempts = async (id: string) => {
    try {
      const rows = await communicationsService.listAttempts(id);
      setAttemptsById((prev) => ({ ...prev, [id]: rows }));
    } catch (err) {
      console.error(err);
      setError('Nu am putut încărca istoricul de delivery.');
    }
  };

  const isChannelConfigured = (channel: CommunicationChannel): boolean => {
    if (!channelStatus) return false;
    if (channel === 'in_app') return Boolean(channelStatus.in_app?.available);
    if (channel === 'email') return Boolean(channelStatus.email?.configured);
    if (channel === 'sms') return Boolean(channelStatus.sms?.configured);
    if (channel === 'push') return Boolean(channelStatus.push?.configured);
    return false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Comunicare
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comunicări reale pe canale in-app/email/SMS/push, cu status delivery și retry.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Canal in-app</Typography>
              <Chip label={isChannelConfigured('in_app') ? 'Disponibil' : 'Indisponibil'} color={isChannelConfigured('in_app') ? 'success' : 'error'} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Canal email</Typography>
              <Chip label={isChannelConfigured('email') ? 'Configurat' : 'Neconfigurat'} color={isChannelConfigured('email') ? 'success' : 'warning'} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Canal SMS</Typography>
              <Chip label={isChannelConfigured('sms') ? 'Configurat' : 'Neconfigurat'} color={isChannelConfigured('sms') ? 'success' : 'warning'} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Canal push</Typography>
              <Chip label={isChannelConfigured('push') ? 'Configurat' : 'Neconfigurat'} color={isChannelConfigured('push') ? 'success' : 'warning'} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Trimite comunicare
          </Typography>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Client ID"
                value={form.clientId}
                onChange={(e) => setForm((prev) => ({ ...prev, clientId: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Appointment ID (opțional)"
                value={form.appointmentId}
                onChange={(e) => setForm((prev) => ({ ...prev, appointmentId: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Canal"
                value={form.channel}
                onChange={(e) => setForm((prev) => ({ ...prev, channel: e.target.value as CommunicationChannel }))}
              >
                {availableChannels.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Template"
                value={form.templateKey}
                onChange={(e) => setForm((prev) => ({ ...prev, templateKey: e.target.value }))}
              >
                <MenuItem value="">(fără template)</MenuItem>
                {templates
                  .filter((t) => t.channel === form.channel)
                  .map((t) => (
                    <MenuItem key={t.id} value={t.template_key}>
                      {t.template_key}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={sendCommunication}
                disabled={sending || (form.channel !== 'in_app' && !isChannelConfigured(form.channel))}
              >
                {sending ? 'Se trimite...' : 'Trimite'}
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Subiect (email/in-app)"
                value={form.subject}
                onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Conținut (sau lasă gol pentru template)"
                value={form.content}
                onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Log comunicări (date reale din backend)</Typography>
            <Button onClick={loadLogs} variant="outlined" disabled={loading}>
              Reîncarcă
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : logs.length === 0 ? (
            <Alert severity="info">Nu există loguri de comunicare pentru criteriile curente.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Canal</TableCell>
                    <TableCell>Status</TableCell>
                            <TableCell>Template</TableCell>
                    <TableCell>Conținut (snapshot)</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Programare</TableCell>
                    <TableCell>Creat la</TableCell>
                            <TableCell>Retry</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((item) => (
                            <TableRow key={item.id}>
                      <TableCell>{item.channel}</TableCell>
                      <TableCell>
                                <Chip
                                  size="small"
                                  label={item.status || 'unknown'}
                                  color={item.status === 'failed' ? 'error' : item.status === 'sent' || item.status === 'delivered' ? 'success' : 'default'}
                                />
                      </TableCell>
                              <TableCell>{item.templateKey || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.contentSnapshot || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.clientId || '-'}</TableCell>
                      <TableCell>{item.appointmentId || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDateOnly(item.createdAt)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeOnly(item.createdAt)}
                        </Typography>
                      </TableCell>
                              <TableCell>
                                <Stack direction="column" spacing={0.5}>
                                  <Typography variant="caption">#{item.retryCount || 0}</Typography>
                                  {item.status === 'failed' && (
                                    <Button size="small" variant="outlined" onClick={() => retryCommunication(item.id)}>
                                      Retry
                                    </Button>
                                  )}
                                  <Button size="small" onClick={() => loadAttempts(item.id)}>
                                    Delivery
                                  </Button>
                                </Stack>
                              </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

              {Object.entries(attemptsById).map(([communicationId, attempts]) => (
                <Card key={communicationId} sx={{ borderRadius: 2, mt: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Delivery attempts pentru {communicationId}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Provider</TableCell>
                          <TableCell>Eroare</TableCell>
                          <TableCell>Moment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(attempts as any[]).map((attempt) => (
                          <TableRow key={attempt.id}>
                            <TableCell>{attempt.attempt_no}</TableCell>
                            <TableCell>{attempt.status}</TableCell>
                            <TableCell>{attempt.provider || '-'}</TableCell>
                            <TableCell>{attempt.error_message || '-'}</TableCell>
                            <TableCell>{new Date(attempt.attempted_at).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
    </Box>
  );
};

export default CommunicationManagement;

